import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDBodyText, FDClickableText, FDStack, FDTitleText,
} from '@facet-wealth/design';
import { uploadFileRaw } from 'Helpers/File';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { noop } from 'Helpers/PropTypes';
import { dataAttrs } from 'Helpers/Testing';
import useMobileLayout from 'Hooks/useMobileLayout';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: theme.radius.md,
    border: `1px dashed ${theme.palette.primary.main}`,
    display: 'block',
  },
  error: {
    borderColor: theme.palette.facet.facetDesignErrorMain,
  },
  dropArea: {
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : 'pointer'),
  },
  loadingIndicator: {
    padding: 0,
  },
}));

const FILE_UPLOAD_SVG_URL = 'https://static.facetwealth.com/image/upload.svg';

export default function FileUploadDropArea({
  url,
  acceptedFileTypes,
  multiple,
  title,
  trailingTitleText,
  subtitle,
  onSuccess,
  onError,
  errorMessage,
  setUploadProgress,
  uploadProgress,
  onUploadStart,
  onUploadEnd,
}) {
  const isMobile = useMobileLayout();

  let titleText = <><FDClickableText appearance={'standalone'} label={'Click to upload'} /> {!isMobile ? 'or drag-and-drop' : ''} {trailingTitleText}</>;
  if (title) {
    titleText = <>{title}{` ${trailingTitleText}`}</>;
  }
  const inputRef = useRef(null);

  const { displayErrorMessage } = useSnackbarContext();

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadComplete = uploadProgress === 100 && !multiple;

  // this component is disabled during the upload process and once the upload is complete successfully
  const disabled = uploading || uploadComplete;

  const classes = useStyles({ dragActive, disabled });

  async function uploadFiles(files, e) {
    if (!files || files.length === 0) return;

    await onUploadStart();

    const fileArray = [];
    for (let i = 0; i < files.length; i++) {
      fileArray.push(files.item(i));
    }

    setUploadProgress(0);
    setUploading(true);

    try {
      await uploadFileRaw(
        url,
        fileArray,
        null,
        setUploadProgress,
      );
      await onSuccess();
    } catch (err) {
      displayErrorMessage(err, err.response?.data?.errors[0]?.detail || 'Failed to upload the file');
      setUploadProgress(0);
      await onError(err);
    } finally {
      setUploading(false);
      await onUploadEnd();

      // Reset the inputs value, this is CRITICAL to do to ensure that the same file can be uploaded again if it fails
      // Do not remove this, it is required for the file upload to work correctly
      e.target.value = '';
    }
  }

  function handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    setDragActive(false);
    if (e.dataTransfer?.files) {
      await uploadFiles(e.dataTransfer.files, e);
    }
  }

  return (
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={classNames(classes.root, { [classes.error]: !!errorMessage })}>
      <input
        ref={inputRef}
        type="file"
        disabled={disabled}
        style={{ display: 'none' }}
        accept={acceptedFileTypes?.toString()}
        onChange={async (e) => {
          await uploadFiles(e.target.files, e);
        }}
        multiple={multiple}
      />
      <div
        className={classes.dropArea}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <FDStack
          paddingY={5}
          paddingX={3}
          direction={'column'}
          alignItems={'center'}
          justifyContent="flex-start"
          spacing={3}
          {...dataAttrs('file-upload-drop-area')}
        >
          <img src={FILE_UPLOAD_SVG_URL} alt={'file-upload'} />
          {!uploadComplete && (
            <FDStack direction={'column'} spacing={1} alignItems={'center'}>
              <FDTitleText value={titleText} />
              <FDBodyText value={subtitle} size={'sm'} />
            </FDStack>
          )}
          {uploadComplete && <FDTitleText value={'Upload Complete'} />}
        </FDStack>
      </div>
    </label>
  );
}
FileUploadDropArea.defaultProps = {
  title: undefined,
  trailingTitleText: 'files',
  acceptedFileTypes: undefined,
  multiple: false,
  onSuccess: noop,
  onError: noop,
  errorMessage: undefined,
  setUploadProgress: noop,
  uploadProgress: 0,
  subtitle: '',
  onUploadStart: noop,
  onUploadEnd: noop,
};
FileUploadDropArea.propTypes = {
  url: PropTypes.string.isRequired,
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
  multiple: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  trailingTitleText: PropTypes.string,
  subtitle: PropTypes.string,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  errorMessage: PropTypes.string,
  setUploadProgress: PropTypes.func,
  uploadProgress: PropTypes.number,
  onUploadStart: PropTypes.func,
  onUploadEnd: PropTypes.func,
};
